import { useMemo, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import type { FeatureStatus } from '../../types';
import type { BulkFeatureItem } from '../../types/admin';
import { adminFeatureApi } from '../../api/adminApi';
import { getApiErrorMessage } from '../../api/client';
import { notify } from '../../components/Notifier';
import { FEATURE_STATUS_META, FEATURE_STATUS_OPTIONS } from './statusMeta';

/** Mẫu JSON hiển thị làm placeholder / gợi ý cấu trúc đầy đủ các field. */
const TEMPLATE_JSON = `{
  "features": [
    {
      "name": "Đăng nhập",
      "description": "Cho phép người dùng đăng nhập bằng email",
      "status": "DRAFT",
      "order_index": 0
    },
    {
      "name": "Đăng xuất",
      "description": null,
      "status": "ACTIVE"
    }
  ]
}`;

/** Một dòng đã được validate phía client để hiển thị màn review. */
interface PreviewRow {
  index: number;
  name: string;
  description: string | null;
  status: FeatureStatus;
  /** null = để backend tự đánh số thứ tự. */
  orderIndex: number | null;
  valid: boolean;
  error?: string;
}

const VALID_STATUSES = new Set<string>(FEATURE_STATUS_OPTIONS);

/** Trích mảng features từ JSON đã parse (chấp nhận mảng trần hoặc { features }). */
function extractList(parsed: unknown): unknown[] | null {
  if (Array.isArray(parsed)) return parsed;
  if (parsed && typeof parsed === 'object' && Array.isArray((parsed as { features?: unknown }).features)) {
    return (parsed as { features: unknown[] }).features;
  }
  return null;
}

/** Validate + chuẩn hóa 1 item (đối xứng với backend normalizeFeatureItem). */
function validateItem(raw: unknown, index: number): PreviewRow {
  const base: PreviewRow = {
    index,
    name: '',
    description: null,
    status: 'DRAFT',
    orderIndex: null,
    valid: false,
  };
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
    return { ...base, error: 'Không phải object JSON' };
  }
  const item = raw as Record<string, unknown>;

  if (typeof item.name !== 'string' || item.name.trim().length === 0) {
    return { ...base, error: "Thiếu hoặc sai 'name'" };
  }
  const name = item.name.trim();

  let status: FeatureStatus = 'DRAFT';
  if (item.status !== undefined && item.status !== null && item.status !== '') {
    if (typeof item.status !== 'string' || !VALID_STATUSES.has(item.status)) {
      return { ...base, name, error: "'status' không hợp lệ" };
    }
    status = item.status as FeatureStatus;
  }

  let orderIndex: number | null = null;
  if (item.order_index !== undefined && item.order_index !== null && item.order_index !== '') {
    const n = Number(item.order_index);
    if (!Number.isFinite(n)) {
      return { ...base, name, status, error: "'order_index' phải là số nguyên" };
    }
    orderIndex = Math.trunc(n);
  }

  const description =
    typeof item.description === 'string' && item.description.trim().length > 0
      ? item.description.trim()
      : null;

  return { index, name, description, status, orderIndex, valid: true };
}

interface Props {
  open: boolean;
  phaseId: string;
  onClose: () => void;
  /** Gọi sau khi import thành công để component cha reload danh sách. */
  onImported: () => void | Promise<void>;
}

type Step = 'input' | 'review';

export function FeatureImportDialog({ open, phaseId, onClose, onImported }: Props): JSX.Element {
  const [step, setStep] = useState<Step>('input');
  const [text, setText] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);
  const [rows, setRows] = useState<PreviewRow[]>([]);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validRows = useMemo(() => rows.filter((r) => r.valid), [rows]);
  const invalidCount = rows.length - validRows.length;

  const reset = () => {
    setStep('input');
    setText('');
    setParseError(null);
    setRows([]);
    setImporting(false);
  };

  const handleClose = () => {
    if (importing) return;
    reset();
    onClose();
  };

  const handlePickFile = () => fileInputRef.current?.click();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // cho phép chọn lại cùng file
    if (!file) return;
    const content = await file.text();
    setText(content);
    setParseError(null);
  };

  const handlePreview = () => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      setParseError('JSON không hợp lệ — vui lòng kiểm tra lại cú pháp.');
      return;
    }
    const list = extractList(parsed);
    if (!list) {
      setParseError('Cấu trúc phải là một mảng hoặc object dạng { "features": [...] }.');
      return;
    }
    if (list.length === 0) {
      setParseError('Danh sách tính năng trống.');
      return;
    }
    setParseError(null);
    setRows(list.map((item, i) => validateItem(item, i)));
    setStep('review');
  };

  const handleImport = async () => {
    if (validRows.length === 0) return;
    setImporting(true);
    const payload: BulkFeatureItem[] = validRows.map((r) => ({
      name: r.name,
      description: r.description,
      status: r.status,
      ...(r.orderIndex !== null ? { order_index: r.orderIndex } : {}),
    }));
    try {
      const result = await adminFeatureApi.bulkCreate(phaseId, payload);
      const serverErrors = result.errors?.length ?? 0;
      notify(
        serverErrors > 0
          ? `Đã import ${result.created.length} tính năng, ${serverErrors} dòng bị bỏ qua`
          : `Đã import ${result.created.length} tính năng`,
        serverErrors > 0 ? 'warning' : 'success',
      );
      await onImported();
      reset();
      onClose();
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể import tính năng'), 'error');
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Import tính năng hàng loạt</DialogTitle>
      <DialogContent dividers>
        {step === 'input' ? (
          <Stack spacing={2}>
            <Alert severity="info">
              Dán JSON theo mẫu bên dưới (hoặc chọn file <code>.json</code>). Chỉ <b>name</b> là bắt
              buộc; <b>description</b>, <b>status</b>, <b>order_index</b> có thể bỏ trống. Import chỉ
              <b> thêm mới</b>, không ảnh hưởng dữ liệu hiện có.
            </Alert>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/json,.json"
              hidden
              onChange={(e) => void handleFileChange(e)}
            />
            <Box>
              <Button variant="outlined" size="small" startIcon={<UploadFileIcon />} onClick={handlePickFile}>
                Chọn file .json
              </Button>
            </Box>
            <TextField
              label="Nội dung JSON"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={TEMPLATE_JSON}
              fullWidth
              multiline
              minRows={10}
              slotProps={{ input: { sx: { fontFamily: 'monospace', fontSize: 13 } } }}
            />
            {parseError && <Alert severity="error">{parseError}</Alert>}
          </Stack>
        ) : (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1}>
              <Chip
                icon={<CheckCircleIcon />}
                color="success"
                label={`${validRows.length} hợp lệ`}
                size="small"
              />
              {invalidCount > 0 && (
                <Chip icon={<ErrorIcon />} color="error" label={`${invalidCount} lỗi`} size="small" />
              )}
            </Stack>
            {invalidCount > 0 && (
              <Alert severity="warning">
                Các dòng lỗi sẽ được bỏ qua khi import. Bạn có thể quay lại sửa JSON rồi xem trước lại.
              </Alert>
            )}
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={48}>#</TableCell>
                    <TableCell>Tên tính năng</TableCell>
                    <TableCell>Mô tả</TableCell>
                    <TableCell>Trạng thái</TableCell>
                    <TableCell width={90}>Thứ tự</TableCell>
                    <TableCell>Kết quả</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.index} hover>
                      <TableCell>{row.index + 1}</TableCell>
                      <TableCell>{row.name || '—'}</TableCell>
                      <TableCell sx={{ maxWidth: 240 }}>{row.description || '—'}</TableCell>
                      <TableCell>
                        {row.valid ? (
                          <Chip
                            label={FEATURE_STATUS_META[row.status].label}
                            color={FEATURE_STATUS_META[row.status].color}
                            size="small"
                          />
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>{row.orderIndex ?? 'Tự động'}</TableCell>
                      <TableCell>
                        {row.valid ? (
                          <Chip icon={<CheckCircleIcon />} color="success" label="Hợp lệ" size="small" />
                        ) : (
                          <Chip icon={<ErrorIcon />} color="error" label={row.error} size="small" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {step === 'review' && (
          <Button onClick={() => setStep('input')} disabled={importing}>
            Quay lại
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={handleClose} disabled={importing}>
          Hủy
        </Button>
        {step === 'input' ? (
          <Button variant="contained" onClick={handlePreview} disabled={!text.trim()}>
            Xem trước
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={() => void handleImport()}
            disabled={importing || validRows.length === 0}
            startIcon={importing ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            Import ({validRows.length} tính năng)
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
